import {
  createFileRoute,
  Link,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { assertOk, isErr } from "@/lib/errors";
import { volume } from "@/lib/volume-api-2";
import { zapier } from "@/lib/zapier-api";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod/v4";
import { toast } from "sonner";
import { zodValidator } from "@tanstack/zod-adapter";
import { phoneNumberValidator } from "@/lib/validation";
import { logger } from "@/lib/logger";
import { AuthCard } from "@/routes/auth/_auth-layout/-components/auth/auth-card";

export const Route = createFileRoute("/auth/_auth-layout/signup_")({
  component: RouteComponent,
  validateSearch: zodValidator(
    z.object({
      utm_source: z.string().optional(),
      utm_medium: z.string().optional(),
      utm_campaign: z.string().optional(),
    }),
  ),
});

const signUpFormSchema = z.object({
  email: z
    .email({ message: "Please enter a valid email address" })
    .toLowerCase(),
  company: z.string().nonempty({ message: "Company name is required" }),
  fullName: z.string().nonempty({ message: "Full name is required" }),
  phoneNumber: phoneNumberValidator,
  utm_source: z.string().optional(),
  utm_medium: z.string().optional(),
  utm_campaign: z.string().optional(),
});

function RouteComponent() {
  const navigate = useNavigate();
  const searchParams = useSearch({ from: "/auth/_auth-layout/signup_" });
  const form = useForm({
    resolver: zodResolver(signUpFormSchema),
    defaultValues: {
      email: "",
      company: "",
      fullName: "",
      phoneNumber: "",
      utm_source: searchParams.utm_source,
      utm_medium: searchParams.utm_medium,
      utm_campaign: searchParams.utm_campaign,
    }, // Just so react doesn't cry of uncontrolled inputs
  });

  const signUpMutation = useMutation({
    mutationKey: ["signUp"],
    mutationFn: (data: z.infer<typeof signUpFormSchema>) => {
      const volumeResponse = volume.merchant.CreateMerchantAccount({
        companyName: data.company,
        email: data.email,
        fullName: data.fullName,
        phoneNumber: data.phoneNumber,
      });

      const zapierResponse = zapier.notify.Trigger({
        email: data.email,
        companyName: data.company,
        name: data.fullName,
        phoneNumber: data.phoneNumber,
        utm_medium: data.utm_medium,
        utm_source: data.utm_source,
        utm_campaign: data.utm_campaign,
      });

      assertOk(volumeResponse);

      if (isErr(zapierResponse)) {
        logger.error("Error sending zapier webhook", {
          zapierResponse,
        });
      }

      return volumeResponse;
    },
  });

  const onSubmit = async (data: z.infer<typeof signUpFormSchema>) => {
    await signUpMutation.mutateAsync(data, {
      onSettled: (data) => {
        if (isErr(data)) {
          if (data.type === "ACCOUNT_EXISTS") {
            toast.error(
              <div>
                An account with this email already exists, please{" "}
                <Link className="underline" to="/auth/signin">
                  Sign In.
                </Link>
              </div>,
            );

            return;
          }
          toast.error("There was an error signing up, please try again.");
        } else {
          navigate({ to: "/auth/signup/success" });
        }
      },
      onError: () => {
        toast.error("There was an error signing up, please try again.");
      },
    });
  };
  return (
    <AuthCard
      backTo="/auth/signin"
      title="Create your Volume account"
      description="Set up your merchant profile to get started with Volume."
      footer={
        <>
          By clicking Register, you agree to our{" "}
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="https://www.getvolume.com/terms-and-conditions"
            className="font-semibold text-volume-text-purple hover:underline"
          >
            Terms and Conditions
          </a>{" "}
          and{" "}
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="https://www.getvolume.com/privacy"
            className="font-semibold text-volume-text-purple hover:underline"
          >
            Privacy Policy
          </a>
          .
        </>
      }
    >
      <Form {...form}>
        <form
          className="flex w-full flex-col items-center"
          onSubmit={(event) => void form.handleSubmit(onSubmit)(event)}
        >
          <div className="flex w-full flex-col items-center text-left">
            <div className="grid w-full items-center gap-1.5">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold">
                      Email address
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        autoComplete="email"
                        placeholder="name@company.com"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="mt-4 grid w-full items-center gap-1.5">
              <FormField
                control={form.control}
                name="company"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold">
                      Company name
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        autoComplete="organization"
                        placeholder="eg. Volume"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="mt-4 grid w-full items-center gap-1.5">
              <FormField
                control={form.control}
                name="fullName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold">Full name</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        autoComplete="name"
                        placeholder="eg. John Smith"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="mt-4 grid w-full items-center gap-1.5">
              <FormField
                control={form.control}
                name="phoneNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold">
                      Phone number
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="tel"
                        autoComplete="tel"
                        placeholder="eg. +44 123 123 123"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Button
              className="mt-6 w-full p-6 text-lg shadow-md shadow-volume-mid-purple/25"
              type="submit"
            >
              {signUpMutation.isPending ? (
                <Spinner height={48} width={48} white={true} />
              ) : (
                <>Sign up</>
              )}
            </Button>
          </div>
        </form>
      </Form>
      <div className="mt-4 self-center text-sm text-V_Base90">
        Already have an account?{" "}
        <Link
          to="/auth/signin"
          className="font-semibold text-volume-text-purple hover:underline"
        >
          Sign in
        </Link>
      </div>
    </AuthCard>
  );
}
